<template>
  <main class="main page-inside text-page">
    <div class="section text-section">
      <div class="page-inside-top">
        <Transition>
          <div v-if="pageContent" class="wrapper">
            <div class="title">
              {{ pageContent.title }}
            </div>
            <div class="desc text-page-header" v-html="pageContent.header" />
            <div class="desc text-page-content" v-html="pageContent.content" />
          </div>
        </Transition>
      </div>
    </div>
  </main>
</template>

<script>
import { mapState, mapActions } from "vuex";

export default {
  name: "TextPage",
  data() {
    return {
      pageContent: null,
      loading: false,
    };
  },
  computed: {
    ...mapState("app", ["currentLanguage"]),
  },
  watch: {
    "$route.params.id": {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.loadPageContent();
        }
      },
    },
    currentLanguage: {
      handler() {
        if (this.$route.params.id) {
          this.loadPageContent();
        }
      },
    },
  },
  methods: {
    ...mapActions("staticPages", ["fetchPageContent"]),
    async loadPageContent() {
      try {
        this.loading = true;
        this.pageContent = null; // Clear old content while loading

        // Use native fetch to avoid circular dependencies
        const id = this.$route.params.id;
        const langId = this.currentLanguage;

        const content = await this.fetchPageContent({ id, langId });
        this.pageContent = content;
      } catch (error) {
        console.error("Failed to load page content:", error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.text-section {
  padding: 0 16px;
}
.section .wrapper {
  padding-top: 60px;
}

.text-page .title {
  margin-bottom: 20px;
}

.text-page .text-section a {
  color: #15ca5c;
}

// Basic text styles
.text-page p {
  margin-bottom: 15px;
  line-height: 1.2;
}

.text-page strong {
  font-weight: bold;
}

.text-page em {
  font-style: italic;
}

.text-page ul {
  list-style: disc;
  padding-left: 30px;
  margin-bottom: 15px;
}

.text-page ol {
  list-style: decimal;
  padding-left: 30px;
  margin-bottom: 15px;
}

.text-page li {
  margin-bottom: 8px;
}

.text-page h1 {
  font-size: 2.2em;
  margin-bottom: 20px;
  font-weight: bold;
}

.text-page h2 {
  font-size: 1.8em;
  margin-bottom: 18px;
  font-weight: bold;
}

.text-page h3 {
  font-size: 1.5em;
  margin-bottom: 16px;
  font-weight: bold;
}

.text-page h4 {
  font-size: 1.3em;
  margin-bottom: 14px;
  font-weight: bold;
}

.text-page h5 {
  font-size: 1.1em;
  margin-bottom: 12px;
  font-weight: bold;
}

.text-page h6 {
  font-size: 1em;
  margin-bottom: 10px;
  font-weight: bold;
}

.text-page blockquote {
  border-left: 4px solid #ccc;
  padding-left: 20px;
  margin: 20px 0;
  font-style: italic;
}

.text-page hr {
  border: 0;
  border-top: 1px solid #ddd;
  margin: 20px 0;
}

.text-page table {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
}

.text-page th,
.text-page td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

.text-page th {
  background-color: #f2f2f2;
}

/* Apply styles to content within text-page-header and text-page-content */
.text-page :deep(.text-page-header p),
.text-page :deep(.text-page-content p) {
  margin-bottom: 15px;
  line-height: 1.2;
}

.text-page :deep(.text-page-header strong),
.text-page :deep(.text-page-content strong) {
  font-weight: bold;
}

.text-page :deep(.text-page-header em),
.text-page :deep(.text-page-content em) {
  font-style: italic;
}

.text-page :deep(.text-page-header ul),
.text-page :deep(.text-page-content ul) {
  list-style: disc;
  padding-left: 30px;
  margin-bottom: 15px;
}

.text-page :deep(.text-page-header ol),
.text-page :deep(.text-page-content ol) {
  list-style: decimal;
  padding-left: 30px;
  margin-bottom: 15px;
}

.text-page :deep(.text-page-header li),
.text-page :deep(.text-page-content li) {
  margin-bottom: 8px;
}

.text-page :deep(.text-page-header a),
.text-page :deep(.text-page-content a) {
  color: #9d1616;
}

.text-page :deep(.text-page-header h1),
.text-page :deep(.text-page-content h1) {
  font-size: 2.2em;
  margin-bottom: 20px;
  font-weight: bold;
}

.text-page :deep(.text-page-header h2),
.text-page :deep(.text-page-content h2) {
  font-size: 1.8em;
  margin-bottom: 18px;
  font-weight: bold;
}

.text-page :deep(.text-page-header h3),
.text-page :deep(.text-page-content h3) {
  font-size: 1.5em;
  margin-bottom: 16px;
  font-weight: bold;
}

.text-page :deep(.text-page-header h4),
.text-page :deep(.text-page-content h4) {
  font-size: 1.3em;
  margin-bottom: 14px;
  font-weight: bold;
}

.text-page :deep(.text-page-header h5),
.text-page :deep(.text-page-content h5) {
  font-size: 1.1em;
  margin-bottom: 12px;
  font-weight: bold;
}

.text-page :deep(.text-page-header h6),
.text-page :deep(.text-page-content h6) {
  font-size: 1em;
  margin-bottom: 10px;
  font-weight: bold;
}

.text-page :deep(.text-page-header blockquote),
.text-page :deep(.text-page-content blockquote) {
  border-left: 4px solid #ccc;
  padding-left: 20px;
  margin: 20px 0;
  font-style: italic;
}

.text-page :deep(.text-page-header hr),
.text-page :deep(.text-page-content hr) {
  border: 0;
  border-top: 1px solid #ddd;
  margin: 20px 0;
}

.text-page :deep(.text-page-header table),
.text-page :deep(.text-page-content table) {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
}

.text-page :deep(.text-page-header th),
.text-page :deep(.text-page-content th),
.text-page :deep(.text-page-header td),
.text-page :deep(.text-page-content td) {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

.text-page :deep(.text-page-header th),
.text-page :deep(.text-page-content th) {
  background-color: #f2f2f2;
}
</style>
